import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Map, Printer, Search, Trash2, Square } from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { EmailButton } from "@/components/EmailButton";
import { HandoffButton } from "@/components/HandoffButton";
import { getBusiness, saveBusiness, businessForAi, belongsToCurrent } from "@/data/business";
import { getSections, formatAnswer } from "@/data/questionnaire";
import { loadAll, loadTerritories, upsertTerritory, removeTerritory, formatDate } from "@/lib/storage";
import { authHeaders, handleUnauthorized } from "@/lib/pin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const summaryFromLatest = () => {
  const sub = loadAll().filter((s) => s.status === "completed" && belongsToCurrent(s))[0];
  if (!sub) return "";
  const qs = getSections(sub.businessType).flatMap((s) => s.questions);
  return ["q1", "q2", "q3", "q5", "q6", "q19", "q20", "q22", "q24"].map((id) => { const q = qs.find((x) => x.id === id); const v = q && formatAnswer(q, sub.answers[id]); return v ? `- ${q.text} ${v}` : ""; }).filter(Boolean).join("\n");
};

export default function TerritoryPage() {
  const navigate = useNavigate();
  const biz = getBusiness();
  const [zona, setZona] = useState(biz.zona || "");
  const [note, setNote] = useState("");
  const [list, setList] = useState(() => loadTerritories().filter(belongsToCurrent));
  const [current, setCurrent] = useState(() => loadTerritories().filter(belongsToCurrent)[0] || null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const poll = async (t, jobId) => {
    let acc = "";
    for (;;) {
      if (abortRef.current?.signal.aborted) return;
      await new Promise((r) => setTimeout(r, 3000));
      let res;
      try {
        res = await fetch(`${API}/territorio/${jobId}`, { headers: authHeaders() });
      } catch {
        continue;
      }
      if (res.status === 404) throw new Error("Ricerca non trovata sul server");
      handleUnauthorized(res);
      if (!res.ok) continue;
      const d = await res.json();
      acc = d.text || "";
      setStatus(d.step || "");
      setCurrent({ ...t, text: acc });
      if (d.status === "error") throw new Error(d.error || "Ricerca interrotta");
      if (d.status === "done") {
        upsertTerritory({ ...t, text: acc, done: true, jobId });
        setCurrent({ ...t, text: acc, done: true, jobId });
        setList(loadTerritories().filter(belongsToCurrent));
        toast.success("Ricerca del territorio completata");
        return;
      }
    }
  };

  const run = async () => {
    const z = zona.trim();
    if (!z) return;
    if (z !== biz.zona) saveBusiness({ ...biz, zona: z });
    const t = { id: crypto.randomUUID(), businessId: biz.id, businessName: biz.name, zona: z, note: note.trim(), createdAt: new Date().toISOString(), text: "", done: false };
    upsertTerritory(t);
    setCurrent(t);
    setList(loadTerritories().filter(belongsToCurrent));
    setError("");
    setStatus("Avvio della ricerca");
    setStreaming(true);
    abortRef.current = new AbortController();
    try {
      const res = await fetch(`${API}/territorio`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ zona: z, note: t.note, business: businessForAi(biz), sintesi_questionario: summaryFromLatest() }) });
      handleUnauthorized(res);
      if (res.status === 429) throw new Error("Troppe richieste: riprova tra qualche minuto");
      if (!res.ok) throw new Error(`Errore del server (${res.status})`);
      const { id } = await res.json();
      upsertTerritory({ ...t, jobId: id });
      await poll(t, id);
    } catch (e) {
      setError(e.message);
      upsertTerritory({ ...t, done: false });
    } finally {
      setStreaming(false);
    }
  };

  const stop = () => { abortRef.current?.abort(); setStreaming(false); if (current) upsertTerritory({ ...current, done: false }); };
  const del = (id) => { if (!window.confirm("Eliminare questa ricerca?")) return; removeTerritory(id); const l = loadTerritories().filter(belongsToCurrent); setList(l); if (current?.id === id) setCurrent(l[0] || null); };
  const doc = () => ({ kind: "territory", title: `${biz.name} · ricerca del territorio`, subtitle: `${current.zona} · ${formatDate(current.createdAt)}`, blocks: [{ markdown: current.text }] });

  return (
    <Shell wide header={
      <div className="flex flex-wrap gap-3 no-print">
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="territory-back-btn"><ArrowLeft size={16} /> Elenco</button>
        {current?.done && <EmailButton className="btn btn-ghost !min-h-[44px] text-sm" build={doc} />}
        {current?.done && <HandoffButton className="btn btn-ghost !min-h-[44px] text-sm" build={doc} />}
        {current?.text && <button className="btn btn-olive !min-h-[44px] text-sm" onClick={() => window.print()} data-testid="territory-print-btn"><Printer size={16} /> Stampa / PDF</button>}
      </div>
    }>
      <div className="fade-up">
        <div className="flex items-center gap-3"><Map className="text-[#2D4030]" size={24} /><p className="eyebrow">Ricerca del territorio</p></div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">I migliori della zona, misurati</h1>
        <p className="text-[#5C4D46] mt-3 max-w-2xl">L'AI cerca sul web le strutture concorrenti più rilevanti per <strong>{biz.name}</strong>, ne misura target, offerta, prezzi, visibilità e recensioni, e indica dove c'è spazio per distinguersi. Fonti citate in fondo.</p>

        <div className="card p-5 sm:p-6 mt-8 grid gap-4 no-print" data-testid="territory-form">
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[#2C221E]">Zona di ricerca <span className="font-normal text-[#7A6B63]">(salvata nell'attività)</span></span>
            <input className="field" value={zona} onChange={(e) => setZona(e.target.value)} placeholder="es. Chianti, Toscana · entro 40 km da Siena · Val d'Orcia" data-testid="territory-zona-input" />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-[#2C221E]">Indicazioni per la ricerca <span className="font-normal text-[#7A6B63]">(facoltative)</span></span>
            <input className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder="es. solo strutture con piscina e camere; confronta i prezzi dei matrimoni" data-testid="territory-note-input" />
          </label>
          <div className="flex flex-wrap gap-3 items-center">
            {streaming ? (
              <button className="btn btn-ghost" onClick={stop} data-testid="territory-stop-btn"><Square size={16} /> Ferma</button>
            ) : (
              <button className="btn btn-primary" onClick={run} disabled={!zona.trim()} data-testid="territory-run-btn"><Search size={18} /> Avvia la ricerca</button>
            )}
            <span className="text-sm text-[#7A6B63]">Richiede 2-4 minuti: l'AI consulta fino a 8 fonti web. Puoi anche cambiare pagina: il risultato resta salvato.</span>
          </div>
        </div>

        {error && <div className="rounded-xl border border-[#A83232]/30 bg-[#A83232]/5 p-4 text-[#A83232] mt-4" data-testid="territory-error">{error}</div>}

        <div className="grid lg:grid-cols-5 gap-6 mt-8">
          <section className="card p-6 sm:p-9 lg:col-span-4 min-w-0" data-testid="territory-content">
            {!current && <p className="text-[#7A6B63]">Nessuna ricerca ancora per questa attività. Scrivi la zona e avvia.</p>}
            {current && (
              <>
                <p className="eyebrow">{current.zona} · {formatDate(current.createdAt)}</p>
                {!current.text && streaming && <p className="text-[#7A6B63] cursor-blink mt-4" data-testid="territory-status">{status || "Sto cercando le strutture della zona"}</p>}
                <div className={`report mt-4 ${streaming ? "cursor-blink" : ""}`} data-testid="territory-text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.text}</ReactMarkdown>
                </div>
              </>
            )}
          </section>
          <aside className="grid gap-3 content-start no-print" data-testid="territory-archive">
            <p className="eyebrow">Ricerche salvate</p>
            {list.length === 0 && <p className="text-sm text-[#B0A096] italic">Nessuna</p>}
            {list.map((t) => (
              <div key={t.id} className="flex items-center gap-2">
                <button className="choice flex-1 !min-h-[52px] text-left" data-selected={current?.id === t.id} onClick={() => setCurrent(t)} data-testid={`territory-item-${t.id}`}>
                  <span><span className="font-semibold block">{t.zona}</span><span className="text-xs text-[#7A6B63]">{formatDate(t.createdAt)}{t.done ? "" : " · incompleta"}</span></span>
                </button>
                <button className="w-10 h-10 grid place-items-center rounded-full text-[#8A7970] hover:bg-[#F3ECE1]" onClick={() => del(t.id)} aria-label="Elimina" data-testid={`territory-delete-${t.id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </Shell>
  );
}
